import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Link2, Copy, Send, RotateCw, Clock, Eye, Users, ExternalLink } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

// The one client link for this bid's contact: what was released, when it was opened, and the Release button.
export default function ClientLinkPanel({ bid, canManage, onReleased }) {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const bidId = bid.bid_id;
  const load = useCallback(() => {
    api.get(`/bids/${bidId}/portal`).then((r) => setP(r.data)).catch(() => setP(null));
  }, [bidId]);
  useEffect(() => { load(); }, [load, bid.status, bid.approval_status]);

  const release = async () => {
    setBusy(true);
    try { const r = await api.post(`/bids/${bidId}/portal/release`, {}); toast.success(`Released ${r.data.release.items.length} document(s) to ${r.data.link.contact?.name || "the client"}`); load(); onReleased && onReleased(); }
    catch (e) { const d = e.response?.data?.detail; toast.error(d?.message || d || "Could not release"); }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    if (!p?.link || !window.confirm("Revoke this client link? The contact sees a Power Ray page asking them to contact Taylor, for every bid on it.")) return;
    try { await api.post(`/client-links/${p.link.link_id}/revoke`); toast.success("Link revoked"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Could not revoke"); }
  };
  const reissue = async () => {
    if (!p?.link || !window.confirm("Re-issue the link? The old link stops working and the contact gets a new one.")) return;
    try { await api.post(`/client-links/${p.link.link_id}/reissue`); toast.success("New link issued"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Could not re-issue"); }
  };
  const copy = () => { if (p?.link?.url) { navigator.clipboard.writeText(p.link.url); toast.success("Link copied"); } };

  if (!p) return null;
  const link = p.link;
  const live = link?.state === "live";
  const opens = (p.activity || []).slice(0, 30);
  return (
    <Card className="p-5 mb-4" data-testid="client-link-panel">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-9 h-9 rounded-lg bg-[#003E87]/[0.08] text-[#003E87] grid place-items-center shrink-0"><Users className="w-5 h-5" /></div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-slate-800">Client link</h3>
          <div className="text-xs text-slate-500">One link for {link?.contact?.name || "the client contact"}: every bid we released to them, and only the documents we sent.</div>
        </div>
        {canManage && (
          <Button size="sm" onClick={release} disabled={busy} className="bg-[#003E87] hover:bg-[#00305F] shrink-0" data-testid="client-link-release">
            <Send className="w-4 h-4 mr-1.5" /> {busy ? "…" : p.released ? "Release again" : "Release to client"}
          </Button>
        )}
      </div>
      {link ? (
        <>
          {link.url ? (
            <div className="flex items-center gap-2 mb-2">
              <input readOnly value={link.url} data-testid="client-link-url" className={`flex-1 min-w-0 text-xs font-mono bg-slate-50 border border-slate-200 rounded px-2.5 py-1.5 truncate ${live ? "text-slate-600" : "text-slate-400 line-through"}`} />
              <Button size="sm" variant="outline" onClick={copy} data-testid="client-link-copy"><Copy className="w-3.5 h-3.5" /></Button>
              <a href={`${link.url}?preview=1`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#003E87] hover:underline shrink-0" data-testid="client-link-view-as-client"><ExternalLink className="w-3.5 h-3.5" /> View as the client</a>
            </div>
          ) : (
            <div className="mb-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2" data-testid="client-link-no-host">{link.url_note || p.host_note || "No client address yet."}</div>
          )}
          <div className="text-xs font-semibold text-slate-700 mb-2" data-testid="client-link-send-state">{p.send_state}</div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600 mb-3" data-testid="client-link-state">
            <span className={`inline-flex items-center gap-1 font-semibold ${live ? "text-emerald-700" : "text-rose-700"}`}><Link2 className="w-3.5 h-3.5" /> {live ? "Live" : link.state === "revoked" ? "Revoked" : "Expired"}</span>
            <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-slate-400" /> Valid through <b data-testid="client-link-expires">{link.expires_at ? new Date(link.expires_at).toLocaleDateString() : "—"}</b> (180 days after the last release)</span>
            {canManage && live && <button onClick={revoke} data-testid="client-link-revoke" className="text-slate-400 hover:text-rose-600">Revoke</button>}
            {canManage && <button onClick={reissue} data-testid="client-link-reissue" className="inline-flex items-center gap-1 text-[#003E87] hover:underline"><RotateCw className="w-3.5 h-3.5" /> Re-issue</button>}
          </div>
        </>
      ) : (
        <div className="text-xs text-slate-500 mb-3" data-testid="client-link-none">No link yet. It is created on the first release to {bid.client_name || "the client"}'s contact.</div>
      )}
      {p.released ? (
        <div className="text-xs text-slate-600 mb-2" data-testid="client-link-released">
          Released: {p.releases[0].items.map((i) => i.label).join(", ")} · {fmt(p.releases[0].at)} by {p.releases[0].by_name} ({p.releases[0].via})
        </div>
      ) : (
        <div className="text-xs text-slate-400 mb-2" data-testid="client-link-unreleased">Nothing released for this bid yet. The link shows “nothing new yet” until approval (when sign-off applies) or Release / Send.</div>
      )}
      {p.last_open && <div className="text-xs text-slate-700 inline-flex items-center gap-1.5" data-testid="client-link-last-open"><Eye className="w-3.5 h-3.5 text-slate-400" /> {p.last_open}</div>}
      {opens.length > 0 && (
        <div className="mt-2 rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-40 overflow-y-auto" data-testid="client-link-activity">
          {opens.map((v) => (
            <div key={v.view_id} className="px-2.5 py-1.5 text-xs flex items-center gap-2 flex-wrap">
              <span className="text-slate-700 font-medium">{fmt(v.at)}</span>
              <span className={v.internal ? "text-slate-400 italic" : "text-slate-600"}>{v.viewer || v.contact_name || "Client"} {v.kind === "download" ? `downloaded ${v.item_label || "a document"}` : "opened the page"}{v.internal ? " · Power Ray test" : ""}</span>
              <span className="text-slate-400">{v.device?.label || ""}{v.city ? ` · ${v.city}` : ""}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
